import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Brain, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const waitForRecoverySession = () =>
      new Promise<{ session: { user: unknown } | null; error: Error | null }>((resolve) => {
        let resolved = false;
        let subscription: { unsubscribe: () => void } | null = null;

        const finish = (session: { user: unknown } | null, error: Error | null) => {
          if (resolved) return;
          resolved = true;
          subscription?.unsubscribe();
          resolve({ session, error });
        };

        const { data: { subscription: sub } } = supabase.auth.onAuthStateChange((event, session) => {
          console.log("ResetPassword: Auth state change", { event, hasSession: !!session });
          if (session?.user) finish(session, null);
        });
        subscription = sub;

        const pollSession = async () => {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (session?.user) finish(session, error);
        };

        pollSession();
        setTimeout(pollSession, 500);
        setTimeout(pollSession, 1000);
        setTimeout(pollSession, 2000);
        setTimeout(async () => {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (session?.user) finish(session, error);
          else finish(null, new Error("Timeout waiting for session"));
        }, 3000);
      });

    const markRecoveryValid = () => {
      setIsValid(true);
      window.history.replaceState(null, "", window.location.pathname);
    };

    const markRecoveryInvalid = async (title: string, description: string) => {
      setIsValid(false);
      await supabase.auth.signOut();
      toast({ title, description, variant: "destructive" });
    };

    const checkSession = async () => {
      if (!isSupabaseConfigured) {
        setIsValidating(false);
        setIsValid(false);
        return;
      }

      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        const error = hashParams.get("error") || queryParams.get("error");
        const errorCode = hashParams.get("error_code") || queryParams.get("error_code");
        const errorDescription = hashParams.get("error_description") || queryParams.get("error_description");
        const accessToken = hashParams.get("access_token");
        const hashType = hashParams.get("type");
        const queryType = queryParams.get("type");
        const pkceCode = queryParams.get("code");
        const tokenHash = queryParams.get("token_hash");

        console.log("ResetPassword: URL params", {
          error,
          errorCode,
          hasAccessToken: !!accessToken,
          hashType,
          queryType,
          hasPkceCode: !!pkceCode,
          hasTokenHash: !!tokenHash,
        });

        if (error && errorCode) {
          setIsValid(false);
          let errorMessage = "Password reset failed";
          if (errorCode === "otp_expired") {
            errorMessage = "The password reset link has expired. Please request a new one.";
          } else if (errorCode === "access_denied") {
            errorMessage = errorDescription
              ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
              : "Access denied. The password reset link may be invalid or expired.";
          } else {
            errorMessage = errorDescription
              ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
              : "An error occurred with the password reset link.";
          }

          toast({
            title: "Password Reset Error",
            description: errorMessage,
            variant: "destructive",
          });
          window.history.replaceState(null, "", window.location.pathname);
          return;
        }

        if (pkceCode) {
          console.log("ResetPassword: Exchanging PKCE code for session");
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(pkceCode);
          if (exchangeError || !data.session?.user) {
            console.error("ResetPassword: PKCE exchange failed", exchangeError);
            await markRecoveryInvalid(
              "Invalid or expired link",
              "This password reset link is invalid or has expired. Please request a new one."
            );
            return;
          }
          markRecoveryValid();
          return;
        }

        if (tokenHash && queryType === "recovery") {
          console.log("ResetPassword: Verifying recovery token_hash");
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: "recovery",
          });
          if (verifyError || !data.session?.user) {
            console.error("ResetPassword: token_hash verification failed", verifyError);
            await markRecoveryInvalid(
              "Invalid or expired link",
              "This password reset link is invalid or has expired. Please request a new one."
            );
            return;
          }
          markRecoveryValid();
          return;
        }

        if (accessToken && hashType === "recovery") {
          const { session, error: sessionError } = await waitForRecoverySession();

          console.log("ResetPassword: Final session check", {
            hasSession: !!session,
            hasUser: !!session?.user,
            error: sessionError?.message,
          });

          if (session?.user && !sessionError) {
            markRecoveryValid();
          } else {
            const errorMsg = sessionError?.message || "Unable to validate the reset link";
            console.error("ResetPassword: Session validation failed", errorMsg);
            await markRecoveryInvalid(
              "Invalid or expired link",
              "This password reset link is invalid or has expired. Please request a new one."
            );
          }
          return;
        }

        console.log("ResetPassword: No valid recovery params", {
          hasAccessToken: !!accessToken,
          hashType,
          queryType,
          hasPkceCode: !!pkceCode,
          hasTokenHash: !!tokenHash,
        });
        setIsValid(false);
        toast({
          title: "Invalid reset link",
          description: "Please use the link from your email to reset your password.",
          variant: "destructive",
        });
      } catch (error) {
        console.error("Error validating reset link:", error);
        setIsValid(false);
        await supabase.auth.signOut();
        toast({
          title: "Error",
          description: "An error occurred while validating the reset link.",
          variant: "destructive",
        });
      } finally {
        setIsValidating(false);
      }
    };

    checkSession();
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    if (!isSupabaseConfigured) {
      toast({
        title: "Service unavailable",
        description: "Authentication service is not configured.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      // Sign out immediately to clear the recovery session (before showing success)
      await supabase.auth.signOut();
      
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated. You can now sign in.",
      });
      
      // Redirect to login after a short delay
      setTimeout(() => {
        setLocation("/login");
      }, 2000);
    } catch (error) {
      toast({
        title: "Failed to update password",
        description: error instanceof Error ? error.message : "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-muted-foreground">Validating reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center">
                <Brain className="h-6 w-6 text-primary-foreground" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl">Invalid reset link</CardTitle>
              <CardDescription>
                This password reset link is invalid or has expired
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              type="button"
              className="w-full"
              onClick={() => setLocation("/forgot-password")}
            >
              Request a new reset link
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setLocation("/login")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center">
              <Brain className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl">Set new password</CardTitle>
            <CardDescription>
              Enter your new password below
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="input-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  data-testid="button-toggle-confirm-password"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              data-testid="button-update-password"
            >
              {isLoading ? "Updating..." : "Update password"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <button
              type="button"
              className="text-primary hover:underline flex items-center justify-center gap-1 mx-auto"
              onClick={() => setLocation("/login")}
              data-testid="link-back-to-login"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to login
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

